import * as THREE from "three";
import InputCommand from "./InputCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CommandStack from "../logic/CommandStack";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import RaycastIntersection from "../logic/RaycastIntersection";
import { SceneManager } from "../model/SceneManager";
import BundleHighlighter, { getProduct, getProductGlobalId } from "../logic/BundleHighlighter";
import { ElementTypeEnum } from "../elementTypeMappings";
import { ElementBundleIndex, getBundleById, getBundleElements, getBundlesForElement, getElementsInBundles, useElementBundlesStore } from "../store/elementBundlesStore";
import { useBundleInspectorStore } from "../store/bundleInspectorStore";
import { useServicesState } from "../store/servicesStateStore";
import { normalizeGuid } from "../utils/guidUtils";
import ICoreBundle from "../interfaces/ICoreBundle";
import SceneObjectData from "../model/sceneObjectData/SceneObjectData";
import { emitter } from "../input/AppEvents";
import { DetachBundleElementsParameters } from "./DetachBundleElementsCmd";
import { hasGbApiRight } from "../store/userRightsStore";
import { GbApiUserRightEnum } from "../enums/GbApiUserRightEnum";

// Walls are never part of a bundle, so they'd only hide the coverings and elements on and behind them
const NON_INSPECTABLE_PRODUCT_TYPES = new Set<ElementTypeEnum>([ElementTypeEnum.Wall]);

// What the pointer can hit while inspecting: products other than walls. Annotations and labels aren't products.
export const isInspectable = (object: THREE.Object3D): boolean => {
    const product = getProduct(object);
    return product !== null && !NON_INSPECTABLE_PRODUCT_TYPES.has(product.productData.productType);
};

// What an element resolves to: its bundles, or just itself when it has none
interface Inspected {
    key: string;                // The same for every element of the same bundles
    bundles: ICoreBundle[];
    globalIds: Set<string>;     // Normalised
}

// The scene objects (annotations) that are visible: the outermost groups carrying their data.
// Ones already hidden, e.g. by the document's scope, are left out along with everything under them.
const findVisibleSceneObjects = (object: THREE.Object3D, found: THREE.Object3D[] = []): THREE.Object3D[] => {
    if (!object.visible) return found;
    if (object.userData instanceof SceneObjectData) {
        found.push(object);
        return found;
    }
    object.children.forEach(child => findVisibleSceneObjects(child, found));
    return found;
};

/**
 * Shows which CoreAPI bundle the hovered element belongs to: highlights every element of the bundle,
 * ghosts the rest of the scene, and names the bundle in a cursor tooltip. Clicking an element selects its
 * bundle in the selection colour and opens the bundle panel; hovering keeps working meanwhile.
 * Scene objects (annotations) are hidden while it runs, so only the model is shown.
 * Itself read-only and not undoable; it runs until cancelled from the running-command panel.
 * Detaching elements runs as DetachBundleElementsCmd on top of it, and it resumes afterwards.
 */
class BundleInspectorCmd extends InputCommand {

    #scene: THREE.Object3D;
    #highlighter: BundleHighlighter;
    #hiddenSceneObjects: THREE.Object3D[] = [];
    #index: ElementBundleIndex | null = null;
    #hovered: Inspected | null = null;
    #pinned: Inspected | null = null;
    #showOnResume: number | null = null;   // The bundle to select when resumed, e.g. the one just detached into
    #ended = false;

    constructor(commandStack: CommandStack,
                canvasInputListener: ICanvasInputListener,
                scene: THREE.Object3D = SceneManager.scene) {
        super(CommandIdEnum.BundleInspector, commandStack, canvasInputListener);
        this.commandNameResourceKey = 'command.bundleInspector.title';
        this.initialPrompResourceKey = 'command.bundleInspector.loading';
        this.#scene = scene;
        this.#highlighter = new BundleHighlighter(scene);
    }

    start() {
        super.start();
        this.addToStack();
        this.connectInput();
        this.hideSceneObjects();
        this.loadBundles();
    }

    connectInput() {
        this.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        // Only the model's products: Select, deactivated underneath, leaves the annotations as targets,
        // and annotations, labels and walls nested in the product groups are filtered out so what's behind is hit
        this.canvasInputListener.resetTargetObjects();
        this.canvasInputListener.setHitFilter(isInspectable);
    }

    canLaunch(commandId: CommandIdEnum): boolean {
        return commandId === CommandIdEnum.DetachBundleElements;
    }

    // Only the ones this command hid are shown again at the end
    hideSceneObjects() {
        this.#hiddenSceneObjects = findVisibleSceneObjects(this.#scene);
        this.#hiddenSceneObjects.forEach(object => object.visible = false);
    }

    showSceneObjects() {
        this.#hiddenSceneObjects.forEach(object => object.visible = true);
        this.#hiddenSceneObjects = [];
    }

    async loadBundles() {
        const dataProvider = useServicesState.getState().dataProvider;
        const index = dataProvider
            ? await useElementBundlesStore.getState().loadIndex(() => dataProvider.loadElementBundles())
            : null;

        if (this.#ended) return;

        this.#index = index;
        // Also what the bar shows when this resumes after a command on top of it
        this.initialPrompResourceKey = index ? 'command.bundleInspector.prompt' : 'command.bundleInspector.loadFailed';
        this.showInfoMessage(this.initialPrompResourceKey);
        if (index) this.canvasInputListener.notifyPointerMoveAtCurrentPosition();
    }

    getCursorName(): string {
        return 'default';
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {
        if (!this.#index) return true;

        const globalId = getProductGlobalId(raycastResult?.object ?? null);
        const hovered = globalId ? this.inspect(this.#index, globalId) : null;
        if (hovered?.key !== this.#hovered?.key) {
            this.#hovered = hovered;
            this.updateHighlight();
        }

        const store = useBundleInspectorStore.getState();
        if (hovered) store.setHoveredBundles(hovered.bundles);
        else store.clearHovered();
        return true;
    }

    // The element's bundles, or just the element when it has none
    inspect(index: ElementBundleIndex, globalId: string): Inspected {
        const bundles = getBundlesForElement(index, globalId);
        if (bundles.length > 0) {
            return {
                key: `bundles:${bundles.map(b => b.id).sort((a, b) => a - b).join(",")}`,
                bundles,
                globalIds: getElementsInBundles(index, bundles),
            };
        }
        const normalized = normalizeGuid(globalId);
        return { key: `element:${globalId}`, bundles, globalIds: new Set(normalized ? [normalized] : []) };
    }

    // The selected bundle in the selection colour, the hovered one in the highlight colour, the rest ghosted.
    // The scene looks normal when there's neither.
    updateHighlight() {
        if (!this.#pinned && !this.#hovered) {
            this.#highlighter.clear();
            return;
        }
        this.#highlighter.apply(this.#pinned?.globalIds ?? new Set(), this.#hovered?.globalIds ?? new Set());
    }

    // Clicking an element selects it and opens its bundle panel. Clicking the selected bundle again,
    // or empty space, clears the selection.
    // Clicks are always consumed, so they don't select anything underneath.
    point(raycastResult: RaycastIntersection | undefined): boolean {
        if (!this.#index) return true;

        const globalId = getProductGlobalId(raycastResult?.object ?? null);
        if (!globalId) {
            this.unpin();
            return true;
        }

        const pinned = this.inspect(this.#index, globalId);
        if (pinned.key === this.#pinned?.key) {
            this.unpin();
            return true;
        }
        this.pin(this.#index, pinned);
        return true;
    }

    pin(index: ElementBundleIndex, pinned: Inspected) {
        this.#pinned = pinned;
        this.updateHighlight();
        const elements = pinned.bundles.length === 1 ? getBundleElements(index, pinned.bundles[0]) : [];
        const onDetach = pinned.bundles.length === 1 && hasGbApiRight(GbApiUserRightEnum.BundlesManage)
            ? () => this.startDetach()
            : null;
        useBundleInspectorStore.getState().setPinned(pinned.bundles, elements, () => this.unpin(), onDetach);
    }

    // The same key as inspect() gives any of its elements
    inspectBundle(index: ElementBundleIndex, bundle: ICoreBundle): Inspected {
        return { key: `bundles:${bundle.id}`, bundles: [bundle], globalIds: getElementsInBundles(index, [bundle]) };
    }

    // From the panel's "…" menu
    startDetach() {
        const bundle = this.#pinned?.bundles.length === 1 ? this.#pinned.bundles[0] : null;
        if (!this.#index || !bundle) return;

        const parameters: DetachBundleElementsParameters = {
            bundle,
            elements: getBundleElements(this.#index, bundle),
            rooms: this.#index.rooms,
            onDetached: (newBundleId) => this.#showOnResume = newBundleId,
        };
        emitter.emit('commandLaunch', { commandId: CommandIdEnum.DetachBundleElements, parameters });
    }

    // A command runs on top: the panel, tooltip and highlight make way for its own. Scene objects stay hidden.
    deactivate() {
        this.disconnectMouse();
        this.canvasInputListener.setHitFilter(null);
        this.#hovered = null;
        const store = useBundleInspectorStore.getState();
        store.clearHovered();
        store.clearPinned();
        this.#highlighter.clear();
    }

    // Selects the bundle to show from the current bundles, which may have been reloaded meanwhile:
    // the one detached into, or else the one selected before. It's gone when it isn't in them any more.
    activate() {
        if (this.#ended) return;
        this.connectInput();
        this.#index = useElementBundlesStore.getState().index ?? this.#index;

        const bundleId = this.#showOnResume ?? (this.#pinned?.bundles.length === 1 ? this.#pinned.bundles[0].id : null);
        this.#showOnResume = null;
        const bundle = this.#index && bundleId !== null ? getBundleById(this.#index, bundleId) : null;
        if (this.#index && bundle) this.pin(this.#index, this.inspectBundle(this.#index, bundle));
        else if (this.#index && this.#pinned && bundleId === null) this.pin(this.#index, this.#pinned);
        else {
            this.#pinned = null;
            this.updateHighlight();
        }
        this.canvasInputListener.notifyPointerMoveAtCurrentPosition();
    }

    // From the panel's close button, or a click on empty space or the selected bundle
    unpin() {
        if (!this.#pinned) return;
        this.#pinned = null;
        useBundleInspectorStore.getState().clearPinned();
        this.updateHighlight();
    }

    rightClick(): void {
        // No input plane to switch
    }

    // Works in both 2D and 3D, so it keeps running; the next pointer move re-evaluates the hover.
    // The selected bundle stays selected.
    onViewerModeChange(): void {
        this.#hovered = null;
        useBundleInspectorStore.getState().clearHovered();
        this.updateHighlight();
    }

    end() {
        this.#ended = true;
        this.#pinned = null;
        this.#hovered = null;
        const store = useBundleInspectorStore.getState();
        store.clearPinned();
        store.clearHovered();
        this.#highlighter.dispose();
        this.showSceneObjects();
        this.canvasInputListener.setHitFilter(null);
        this.canvasInputListener.resetTargetObjects();
        super.end();
        this.removeFromStack();
    }
}

export default BundleInspectorCmd;
